import get from 'lodash/get';
import groupBy from 'lodash/groupBy';

import { 
  applicationProductFormats,
  fertilityProductFormats 
} from 'constants/products';
import { applicationProperties } from 'constants/scripts';
import { UNIT_TYPES } from 'constants/unitTypes';

import {
  getFertilizerApplicationsForZone
} from 'scripts/zones/fertility-selectors';
import { getSeedApplicationsForZone } from 'scripts/zones/planting-selectors';
import { area } from 'geometry/geojson/area';

import { convert, createUnit } from 'util/unitConversion';
import { getApplicationsForZoneByType } from '../scripts/zones/selectors';
import { getFormatFromZones } from 'products/selectors';

export const calcAreaForZoneGeoJson = (zoneGeoJson) =>
  convert({
    from: { q: area(zoneGeoJson), u: UNIT_TYPES.hectare},
    to: { u: UNIT_TYPES.acre},
    precision: 5
  });

export const getZonesWithArea = (zones) => {
  return Object.keys(zones).reduce((_newZones, currZoneKey) => ({
    ..._newZones,
    [currZoneKey]: {
      ...zones[currZoneKey],
      meta: {
        ...zones[currZoneKey].meta,
        area: calcAreaForZoneGeoJson(zones[currZoneKey].geoJson)
      }
    }
  }), {});
};

const _getZoneAreaUpdatingIfNeeded = (zone) => {
  if (!zone.meta.area) {
    zone.meta.area = calcAreaForZoneGeoJson(zone.geoJson.geometry);
  }
  return zone.meta.area.q;
};

const _getWeightedSeedAmountForZones = (zones) => {
  const weightedSeedAmount = Object.values(zones).reduce((zoneAccumulator, zone) => {
    const zoneArea = _getZoneAreaUpdatingIfNeeded(zone);
    const seedApplication = getSeedApplicationsForZone(zone)[0];
    const rate = get(seedApplication, `${applicationProperties.RATE}.q`, 0);

    return zoneAccumulator + rate * zoneArea;
  }, 0);
  return { q: weightedSeedAmount, u: UNIT_TYPES.seed };
};

const _getWeightedNitrogenAmountForZones = (zones) => {
  const weightedNitrogenAmount = Object.values(zones).reduce((zoneAccumulator, zone) => {
    const zoneArea = _getZoneAreaUpdatingIfNeeded(zone);
    const fertilizerApplication = getFertilizerApplicationsForZone(zone)[0];
    const nitrogenRate = get(fertilizerApplication, `${applicationProperties.NITROGEN_RATE}.q`);
    if(nitrogenRate) {
      return zoneAccumulator + nitrogenRate * zoneArea;
    }
    return zoneAccumulator;
  }, 0);
  return { q: weightedNitrogenAmount, u: UNIT_TYPES.pound };
};

export const _getWeightedFertilizerAmountForZones = (zones, format, applicationType) => {
  const weightedFertilizerAmount = Object.values(zones).reduce((zoneAccumulator, zone) => {
    const zoneArea = _getZoneAreaUpdatingIfNeeded(zone);
    const fertilizerApplication = getApplicationsForZoneByType({ zone, applicationType })[0];
    const fertilizerRate = get(fertilizerApplication, `${applicationProperties.RATE}.q`);
    return zoneAccumulator + fertilizerRate * zoneArea;
  }, 0);
  const weightedFertilizerAmountUnit = format === fertilityProductFormats.DRY
    ? UNIT_TYPES.pound
    : UNIT_TYPES.gal;
  return { q: weightedFertilizerAmount, u: weightedFertilizerAmountUnit };
};

const _getWeightedSeedTargetYieldForZones = (zones) => {
  const weightedSeedTargetYield = Object.values(zones).reduce((zoneAccumulator, zone) => {
    const zoneArea = _getZoneAreaUpdatingIfNeeded(zone);
    const seedApplication = getSeedApplicationsForZone(zone)[0];
    const targetYield = get(seedApplication, 'target-yield.q', 0);

    return zoneAccumulator + targetYield * zoneArea;
  }, 0);
  return { q: weightedSeedTargetYield, u: UNIT_TYPES.seed_acre};
};

const _getWeightedEstGrossRevenueForZones = (zones) => {
  const weightedEstGrossRevenue = Object.values(zones).reduce((zoneAccumulator, zone) => {
    const profit = get(getSeedApplicationsForZone(zone), `[0].estimated-profit.q`, 0);
    const zoneArea = _getZoneAreaUpdatingIfNeeded(zone);
    return zoneAccumulator + profit * zoneArea;
  }, 0);
  return { q: weightedEstGrossRevenue, u: UNIT_TYPES.usd_acre};
};

const _getWeightedEstYieldForZones = (zones) => {
  const weightedEstYield = Object.values(zones).reduce((zoneAccumulator, zone) => {
    const estYield = get(getSeedApplicationsForZone(zone), `[0].estimated-yield.q`, 0);
    const zoneArea = _getZoneAreaUpdatingIfNeeded(zone);
    return zoneAccumulator + estYield * zoneArea;
  }, 0);
  return { q: weightedEstYield, u: UNIT_TYPES.bu_ac};
};

export const getTotalAreaForZones = (zones) => {
  const area = zones.reduce((total, zone) => {
    return total + _getZoneAreaUpdatingIfNeeded(zone);
  }, 0);
  return { q: area, u: UNIT_TYPES.acre };
};

export const getAvgSeedRateForZones = (zones) => {
  const { q: weightedSeedAmount } = _getWeightedSeedAmountForZones(zones);
  const { q: totalArea } = getTotalAreaForZones(zones);
  return { q: weightedSeedAmount / totalArea, u: UNIT_TYPES.seed_acre };
};

export const getAvgNitrogenRateForZones = (zones) => {
  const { q: weightedNitrogenAmount } = _getWeightedNitrogenAmountForZones(zones);
  const { q: totalArea } = getTotalAreaForZones(zones);
  return { q: weightedNitrogenAmount / totalArea, u: UNIT_TYPES.lbs_ac };
};

export const getAvgProductRateForZones = (zones, applicationType) => {
  const format = getFormatFromZones(zones);
  const { q: weightedFertilizerAmount, u: fertilizerUnits }
    = _getWeightedFertilizerAmountForZones(zones, format, applicationType);
  const { q: totalArea } = getTotalAreaForZones(zones);
  const fertilizerRateUnits = fertilizerUnits === UNIT_TYPES.pound ? UNIT_TYPES.pound_acre : UNIT_TYPES.gal_ac;
  return { q: weightedFertilizerAmount / totalArea, u: fertilizerRateUnits };
};

export const getAvgSeedTargetYieldForZones = (zones) => {
  const { q: weightedSeedTargetYield } = _getWeightedSeedTargetYieldForZones(zones);
  const { q: totalArea } = getTotalAreaForZones(zones);
  return { q: weightedSeedTargetYield / totalArea, u: UNIT_TYPES.bu_ac };
};

export const getAvgEstGrossRevenueForZones = (zones) => {
  const { q: weightedGrossRevenue } = _getWeightedEstGrossRevenueForZones(zones);
  const { q: totalArea } = getTotalAreaForZones(zones);
  return { q: weightedGrossRevenue / totalArea, u: UNIT_TYPES.usd_acre };
};

export const getAvgEstYieldForZones = (zones) => {
  const { q: weightedEstYield } = _getWeightedEstYieldForZones(zones);
  const { q: totalArea } = getTotalAreaForZones(zones);
  return { q: weightedEstYield / totalArea, u: UNIT_TYPES.bu_ac };
};

export const getNumSeedBagsForZones = ({ zones, bagSize }) => {
  const { q: weightedSeedAmount } = _getWeightedSeedAmountForZones(zones);

  const numBags = createUnit(weightedSeedAmount, 'seed').to('bag', { bagSize });

  return {
    q: numBags.scalar,
    u: 'bag'
  };
};

export const getTotalProductForZones = ({ zones, applicationType }) => {

  const format = getFormatFromZones(zones);
  const weightedFertilizerAmount = _getWeightedFertilizerAmountForZones(zones, format, applicationType);
  const weightedApplicationAmount = _getWeightedFertilizerAmountForZones(zones, format, applicationType);

  let totalProduct;

  switch (format) {
    case fertilityProductFormats.DRY:
      totalProduct = convert({ from: weightedFertilizerAmount, to: { u: UNIT_TYPES.short_ton } });
      break;
    case fertilityProductFormats.WET:
      totalProduct = weightedFertilizerAmount;
      break;
    case applicationProductFormats.DRY:
      totalProduct = convert({ from: weightedApplicationAmount, to: { u: UNIT_TYPES.short_ton } });
      break;
    case applicationProductFormats.WET:
      totalProduct = weightedApplicationAmount;
      break;
    default:
  }
  return {...totalProduct, format };
};

export const groupZonesByProduct = ({ zones, applicationType }) => {
  return groupBy(zones, (zone) => {
    return get(getApplicationsForZoneByType({ zone, applicationType})[0], 'product.name', 'unassigned');
  });
};

export const groupZonesByFertilizerProduct = (zones) => {
  return groupBy(zones, (zone) => {
    return get(getFertilizerApplicationsForZone(zone)[0], 'product.name', 'unassigned');
  });
};

export const groupZonesBySeedProduct = (zones) => {
  return groupBy(zones, (zone) => {
    return get(getSeedApplicationsForZone(zone)[0], 'product.name', 'unassigned');
  });
};

export const unitTestPrivate = {
  _getWeightedSeedAmountForZones,
  _getWeightedFertilizerAmountForZones,
  _getWeightedNitrogenAmountForZones,
  _getWeightedSeedTargetYieldForZones,
  _getWeightedEstGrossRevenueForZones,
  _getWeightedEstYieldForZones
};
